import type { Node } from "@xyflow/react";

import type { WorkspaceEdge } from "../../../types/workspace";
import {
  WORKSPACE_ACCOUNT_CARD_SIZE,
  WORKSPACE_NODE_CARD_HEIGHT,
  WORKSPACE_NODE_CARD_WIDTH,
  WORKSPACE_NODE_COLLISION_GAP,
  WORKSPACE_PLACEHOLDER_NODE_SIZE,
  snapWorkspaceNodeByCenter,
} from "./workspaceGraphConstants";
import {
  WORKSPACE_INSERT_NODE_SIZE,
  isWorkspaceEphemeralNodeId,
  isWorkspaceInsertNodeId,
  isWorkspacePlaceholderNodeId,
  syncPlaceholderPositionsToParents,
} from "./workspacePlaceholderUtils";
import {
  isWorkspaceExecutingAccountNodeId,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
} from "./workspaceExecutingAccountUtils";

/** Re-export so drag helpers and layout share one gap constant. */
export { WORKSPACE_NODE_COLLISION_GAP };

type NodeBox = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

function ephemeralConstantSize(nodeId: string): { width: number; height: number } | null {
  if (isWorkspaceInsertNodeId(nodeId)) {
    return { width: WORKSPACE_INSERT_NODE_SIZE, height: WORKSPACE_INSERT_NODE_SIZE };
  }
  if (isWorkspacePlaceholderNodeId(nodeId)) {
    return { width: WORKSPACE_PLACEHOLDER_NODE_SIZE, height: WORKSPACE_PLACEHOLDER_NODE_SIZE };
  }
  if (isWorkspaceExecutingAccountNodeId(nodeId)) {
    return {
      width: WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
      height: WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
    };
  }
  return null;
}

function readNodeBox(node: Node): NodeBox {
  const ephemeral = ephemeralConstantSize(node.id);
  const width =
    ephemeral?.width ??
    (typeof node.width === "number" && Number.isFinite(node.width) && node.width > 0
      ? node.width
      : node.type === "workspace" &&
          (node.data as { workspaceNode?: { type?: string } } | undefined)?.workspaceNode
            ?.type === "account"
        ? WORKSPACE_ACCOUNT_CARD_SIZE
        : WORKSPACE_NODE_CARD_WIDTH);
  const height =
    ephemeral?.height ??
    (typeof node.height === "number" && Number.isFinite(node.height) && node.height > 0
      ? node.height
      : node.type === "workspace" &&
          (node.data as { workspaceNode?: { type?: string } } | undefined)?.workspaceNode
            ?.type === "account"
        ? WORKSPACE_ACCOUNT_CARD_SIZE
        : WORKSPACE_NODE_CARD_HEIGHT);

  return {
    id: node.id,
    x: node.position.x,
    y: node.position.y,
    width,
    height,
  };
}

function boxesOverlapWithGap(a: NodeBox, b: NodeBox, gap: number): boolean {
  return (
    a.x < b.x + b.width + gap &&
    a.x + a.width + gap > b.x &&
    a.y < b.y + b.height + gap &&
    a.y + a.height + gap > b.y
  );
}

function boxBlocked(box: NodeBox, obstacles: NodeBox[], gap: number): boolean {
  return obstacles.some((obstacle) => boxesOverlapWithGap(box, obstacle, gap));
}

/** Soft-tether parent may overlap its own add diamond while the edge stretches.
 * Direct children are also non-obstacles so the diamond can sit in their column
 * below the stack (collision trap must not pin it in the parent→child corridor).
 *
 * Executing-account diamonds park past policy → combined_policy → combined_simulation
 * (see computeExecutingAccountNodePosition). Treat that forward pipeline as anchors
 * too — otherwise the door-trap pins the diamond in the corridor and the edge
 * looks like a stub with no card.
 */
function ephemeralAnchorIds(node: Node, edges: WorkspaceEdge[] = []): Set<string> {
  const ids = new Set<string>();
  if (isWorkspacePlaceholderNodeId(node.id)) {
    const data = node.data as { parentNode?: { id?: string } };
    const parentId = data.parentNode?.id;
    if (parentId) {
      ids.add(parentId);
      for (const edge of edges) {
        if (edge.sourceNodeId === parentId) ids.add(edge.targetNodeId);
      }
    }
  }
  if (isWorkspaceExecutingAccountNodeId(node.id)) {
    const data = node.data as { parentNode?: { id?: string } };
    const parentId = data.parentNode?.id;
    if (parentId) {
      ids.add(parentId);
      const directChildIds: string[] = [];
      for (const edge of edges) {
        if (edge.sourceNodeId !== parentId) continue;
        ids.add(edge.targetNodeId);
        directChildIds.push(edge.targetNodeId);
      }
      // Grandchildren (combined_policy → combined_simulation).
      if (directChildIds.length > 0) {
        const direct = new Set(directChildIds);
        for (const edge of edges) {
          if (direct.has(edge.sourceNodeId)) ids.add(edge.targetNodeId);
        }
      }
    }
  }
  if (isWorkspaceInsertNodeId(node.id)) {
    // Hub lives in the account→report runway (often tighter than COLLISION_GAP when
    // there is no strategy column). Treat the account + its reports as anchors so
    // the collision trap does not eject the hub to the left of the account.
    const data = node.data as {
      accountNode?: { id?: string };
      reportNodes?: Array<{ id?: string }>;
    };
    const accountId = data.accountNode?.id;
    if (accountId) ids.add(accountId);
    for (const report of data.reportNodes ?? []) {
      if (report.id) ids.add(report.id);
    }
    if (accountId) {
      for (const edge of edges) {
        if (edge.sourceNodeId === accountId) ids.add(edge.targetNodeId);
      }
    }
  }
  return ids;
}

/**
 * Push `moving` out of `obstacle` so at least `gap` remains between edges.
 * Uses the shallowest penetration axis (same idea as AABB wall response).
 */
export function separateBoxFromObstacle(
  moving: NodeBox,
  obstacle: NodeBox,
  gap: number
): { x: number; y: number } {
  if (!boxesOverlapWithGap(moving, obstacle, gap)) {
    return { x: moving.x, y: moving.y };
  }

  const pushRight = obstacle.x + obstacle.width + gap - moving.x;
  const pushLeft = moving.x + moving.width + gap - obstacle.x;
  const pushDown = obstacle.y + obstacle.height + gap - moving.y;
  const pushUp = moving.y + moving.height + gap - obstacle.y;

  const minX = Math.min(pushRight, pushLeft);
  const minY = Math.min(pushDown, pushUp);

  if (minX <= minY) {
    return {
      x: pushRight < pushLeft ? moving.x + pushRight : moving.x - pushLeft,
      y: moving.y,
    };
  }

  return {
    x: moving.x,
    y: pushDown < pushUp ? moving.y + pushDown : moving.y - pushUp,
  };
}

/**
 * Separate from an obstacle, preferring the escape direction `(biasX, biasY)`
 * (e.g. back out along the approach path) when several axes clear the gap.
 */
function separateBoxFromObstacleBiased(
  moving: NodeBox,
  obstacle: NodeBox,
  gap: number,
  biasX: number,
  biasY: number
): { x: number; y: number } {
  if (!boxesOverlapWithGap(moving, obstacle, gap)) {
    return { x: moving.x, y: moving.y };
  }

  const pushRight = obstacle.x + obstacle.width + gap - moving.x;
  const pushLeft = moving.x + moving.width + gap - obstacle.x;
  const pushDown = obstacle.y + obstacle.height + gap - moving.y;
  const pushUp = moving.y + moving.height + gap - obstacle.y;

  const candidates = [
    { x: moving.x + pushRight, y: moving.y, bx: 1, by: 0, dist: pushRight },
    { x: moving.x - pushLeft, y: moving.y, bx: -1, by: 0, dist: pushLeft },
    { x: moving.x, y: moving.y + pushDown, bx: 0, by: 1, dist: pushDown },
    { x: moving.x, y: moving.y - pushUp, bx: 0, by: -1, dist: pushUp },
  ];

  const biasLen = Math.hypot(biasX, biasY);
  const nx = biasLen > 0 ? biasX / biasLen : 0;
  const ny = biasLen > 0 ? biasY / biasLen : 0;

  candidates.sort((a, b) => {
    const scoreA = a.bx * nx + a.by * ny;
    const scoreB = b.bx * nx + b.by * ny;
    if (scoreA !== scoreB) return scoreB - scoreA;
    return a.dist - b.dist;
  });

  const best = candidates[0]!;
  return { x: best.x, y: best.y };
}

function separateBoxFromObstacles(
  moving: NodeBox,
  obstacles: NodeBox[],
  gap: number,
  biasX = 0,
  biasY = 0
): { x: number; y: number } {
  let position = { x: moving.x, y: moving.y };
  const useBias = biasX !== 0 || biasY !== 0;
  for (let pass = 0; pass < 4; pass += 1) {
    let live = { ...moving, ...position };
    let moved = false;
    for (const obstacle of obstacles) {
      const next = useBias
        ? separateBoxFromObstacleBiased(live, obstacle, gap, biasX, biasY)
        : separateBoxFromObstacle(live, obstacle, gap);
      if (next.x !== live.x || next.y !== live.y) {
        position = next;
        live = { ...live, x: next.x, y: next.y };
        moved = true;
      }
    }
    if (!moved) break;
  }
  return position;
}

/**
 * Follow `desired` when clear. If blocked, slide from `previous` toward desired
 * and stop at first contact (door-trap). Never rest inside an obstacle.
 */
export function resolveEphemeralFollowPosition(
  desired: { x: number; y: number },
  previous: { x: number; y: number } | undefined,
  size: { width: number; height: number },
  obstacles: NodeBox[],
  gap: number = WORKSPACE_NODE_COLLISION_GAP
): { x: number; y: number } {
  const desiredBox: NodeBox = {
    id: "desired",
    x: desired.x,
    y: desired.y,
    width: size.width,
    height: size.height,
  };
  if (!boxBlocked(desiredBox, obstacles, gap)) {
    return desired;
  }

  let start = previous ?? desired;
  let startBox: NodeBox = {
    id: "start",
    x: start.x,
    y: start.y,
    width: size.width,
    height: size.height,
  };
  if (boxBlocked(startBox, obstacles, gap)) {
    // Back out opposite the follow delta so we exit the way we entered.
    const biasX = start.x - desired.x;
    const biasY = start.y - desired.y;
    start = separateBoxFromObstacles(startBox, obstacles, gap, biasX, biasY);
    startBox = { ...startBox, x: start.x, y: start.y };
  }

  // Desired still blocked: advance from the free start toward desired until contact.
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 16; i += 1) {
    const mid = (lo + hi) / 2;
    const candidate: NodeBox = {
      id: "sweep",
      x: start.x + (desired.x - start.x) * mid,
      y: start.y + (desired.y - start.y) * mid,
      width: size.width,
      height: size.height,
    };
    if (boxBlocked(candidate, obstacles, gap)) {
      hi = mid;
    } else {
      lo = mid;
    }
  }

  return {
    x: start.x + (desired.x - start.x) * lo,
    y: start.y + (desired.y - start.y) * lo,
  };
}

/**
 * Resolve a dragged entity's position against other nodes.
 * Uses a sweep from the previous pose so fast drags cannot tunnel through
 * neighbours, then re-separates after grid snap so snapping cannot re-overlap.
 */
export function resolveWorkspaceNodeDragPosition(
  dragged: Node,
  nodes: Node[],
  gap: number = WORKSPACE_NODE_COLLISION_GAP,
  previousPosition?: { x: number; y: number }
): { x: number; y: number } {
  const size = readNodeBox(dragged);
  const obstacles = nodes
    .filter((node) => {
      if (node.id === dragged.id) return false;
      // Parent may soft-overlap its own add diamond while the edge stretches.
      if (node.id === `placeholder:${dragged.id}`) return false;
      return true;
    })
    .map((node) => readNodeBox(node));

  const desired = { x: dragged.position.x, y: dragged.position.y };
  const swept = resolveEphemeralFollowPosition(
    desired,
    previousPosition ?? desired,
    { width: size.width, height: size.height },
    obstacles,
    gap
  );

  // Snap by center for every node size so handles share one lattice
  // (top-left snap put 80px cards / 72px / 52px hubs on different rows).
  const snapDrag = (pose: { x: number; y: number }) =>
    snapWorkspaceNodeByCenter(pose, { width: size.width, height: size.height });

  let position = snapDrag(swept);
  for (let pass = 0; pass < 4; pass += 1) {
    const moving: NodeBox = {
      id: dragged.id,
      x: position.x,
      y: position.y,
      width: size.width,
      height: size.height,
    };
    if (!boxBlocked(moving, obstacles, gap)) break;
    const separated = separateBoxFromObstacles(moving, obstacles, gap);
    const snapped = snapDrag(separated);
    if (snapped.x === position.x && snapped.y === position.y) {
      // Snap keeps fighting separation — prefer the unsnapped clear pose.
      position = separated;
      break;
    }
    position = snapped;
  }

  return position;
}

/**
 * Sync add/insert diamonds to their parents, but stop them at obstacle contact
 * while the parent can keep moving (door-trap). Resume when the follow pose clears.
 *
 * Executing-account diamonds jump the pipeline: cards entirely left of the
 * desired park column are not door-trap walls (they would pin terminal-policy
 * EAs in the combined corridor). Same-column peers still separate.
 */
export function syncPlaceholderPositionsWithCollisionTrap(
  nodes: Node[],
  edges: WorkspaceEdge[],
  gap: number = WORKSPACE_NODE_COLLISION_GAP
): Node[] {
  const following = syncPlaceholderPositionsToParents(nodes, edges);
  const previousById = new Map(nodes.map((node) => [node.id, node]));

  return following.map((node) => {
    if (!isWorkspaceEphemeralNodeId(node.id)) return node;

    const desired = readNodeBox(node);
    const anchors = ephemeralAnchorIds(node, edges);
    const isExec = isWorkspaceExecutingAccountNodeId(node.id);
    const obstacles = nodes
      .filter((other) => other.id !== node.id && !anchors.has(other.id))
      .map((other) => readNodeBox(other))
      .filter((obstacle) => {
        if (!isExec) return true;
        // Allow teleporting past intervening pipeline cards into the shared EA column.
        return obstacle.x + obstacle.width + gap > desired.x;
      });

    if (isExec) {
      const desiredPose = { x: desired.x, y: desired.y };
      if (!boxBlocked(desired, obstacles, gap)) {
        if (desiredPose.x === node.position.x && desiredPose.y === node.position.y) {
          return node;
        }
        return { ...node, position: desiredPose };
      }
      // Desired overlaps a peer in the EA column — nudge clear (prefer vertical).
      const separated = separateBoxFromObstacles(desired, obstacles, gap, 0, 1);
      if (separated.x === node.position.x && separated.y === node.position.y) return node;
      return { ...node, position: separated };
    }

    const previous = previousById.get(node.id);
    const resolved = resolveEphemeralFollowPosition(
      { x: desired.x, y: desired.y },
      previous ? { x: previous.position.x, y: previous.position.y } : undefined,
      { width: desired.width, height: desired.height },
      obstacles,
      gap
    );

    if (resolved.x === node.position.x && resolved.y === node.position.y) return node;
    return { ...node, position: resolved };
  });
}
